/**
 * panel.tsx — the GenUI experience: a dockable, resizable, draggable surface that
 * replaces the chat console. It is NOT a chat — it shows the shared SCRATCHPAD (typed
 * blocks the assistant and human co-edit) plus a transient activity strip while the
 * assistant works. There is no streamed prose transcript; the assistant's output IS the
 * scratchpad.
 *
 * Three modes, persisted: collapsed (a launcher), floating (drag by header, resize via
 * the native corner), and docked-left (an <aside> beside the main view, width-resizable;
 * the main content makes room via a root class + CSS var). Scratchpad changes are applied
 * inside a View Transition so blocks animate in/out/reorder.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { getState, onDataChanged, streamAgent, cancelAgent, scratchpadOp, type ChatMessage } from "../lib/api.ts";
import { onConsoleRequest } from "../lib/console-bus.ts";
import type { Workspace } from "../../store/events.ts";
import type { ScratchpadBlock } from "../../genui/scratchpad.ts";
import type { BlockData } from "../../genui/blocks.ts";
import { BlockView, type BlockHandlers } from "./blocks.tsx";
import { AutoTextarea } from "../lib/auto-textarea.tsx";
import { navigate, useRoute } from "../lib/router.tsx";
import { useI18n, useT } from "../lib/i18n.tsx";

type Mode = "collapsed" | "floating" | "docked";
interface Geo {
  mode: Mode;
  x: number;
  y: number;
  w: number;
  h: number;
  dockW: number;
}
const KEY = "lineage-genui";
const DEFAULT_GEO = (): Geo => ({ mode: "collapsed", x: Math.max(16, window.innerWidth - 420), y: 96, w: 380, h: 520, dockW: 380 });

function loadGeo(): Geo {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...DEFAULT_GEO(), ...(JSON.parse(raw) as Partial<Geo>) };
  } catch {
    /* ignore */
  }
  return DEFAULT_GEO();
}

/** Run a DOM mutation inside a View Transition when the browser supports it. When a
 * transition is interrupted by the next one (rapid scratchpad updates) the browser
 * rejects its promises with AbortError — swallow those so they aren't unhandled. */
interface ViewTransitionLike {
  finished?: Promise<unknown>;
  ready?: Promise<unknown>;
  updateCallbackDone?: Promise<unknown>;
}
type VTDoc = Document & { startViewTransition?: (cb: () => void) => ViewTransitionLike };
const swallow = (p?: Promise<unknown>): void => void p?.catch(() => {});
function withTransition(fn: () => void): void {
  const doc = document as VTDoc;
  if (typeof doc.startViewTransition !== "function") {
    fn();
    return;
  }
  const vt = doc.startViewTransition(fn);
  swallow(vt?.finished);
  swallow(vt?.ready);
  swallow(vt?.updateCallbackDone);
}

interface Activity {
  name: string;
  ok?: boolean;
  summary?: string;
}

export function ScratchpadPanel(): JSX.Element {
  const t = useT();
  const { locale } = useI18n();
  const route = useRoute();
  const [geo, setGeo] = useState<Geo>(loadGeo);
  const [ws, setWs] = useState<Workspace | null>(null);
  const [blocks, setBlocks] = useState<readonly ScratchpadBlock[]>([]);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState<Activity[]>([]);
  const runIdRef = useRef<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  // Persist geometry/mode.
  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(geo));
    } catch {
      /* ignore */
    }
  }, [geo]);

  // Dock: make the main view make room (root class + CSS var), animated via CSS.
  useEffect(() => {
    const root = document.documentElement;
    if (geo.mode === "docked") {
      root.classList.add("genui-docked");
      root.style.setProperty("--genui-dock-w", `${geo.dockW}px`);
    } else {
      root.classList.remove("genui-docked");
    }
    return () => root.classList.remove("genui-docked");
  }, [geo.mode, geo.dockW]);

  // Read the scratchpad; re-apply inside a View Transition so blocks animate on change.
  const refetch = useCallback(() => {
    void getState().then((w) => {
      withTransition(() =>
        flushSync(() => {
          setWs(w);
          setBlocks(w.scratchpad?.["default"]?.blocks ?? []);
        }),
      );
    });
  }, []);
  useEffect(() => {
    refetch();
    return onDataChanged(refetch);
  }, [refetch]);

  // Trace "replay" (and any console request) pre-fills the composer and opens the panel.
  useEffect(
    () =>
      onConsoleRequest((p) => {
        setGeo((g) => (g.mode === "collapsed" ? { ...g, mode: "floating" } : g));
        setPrompt(p);
      }),
    [],
  );

  // Keep the scroll pinned to the newest content as blocks/activity arrive.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [blocks, activity, geo.mode]);

  // Floating mode resizes via the native CSS grabber; mirror the result back into geo
  // so the controlled inline size matches (otherwise the next render snaps it back).
  useEffect(() => {
    if (geo.mode !== "floating" || !panelRef.current) return;
    const el = panelRef.current;
    const ro = new ResizeObserver(() => {
      const w = Math.round(el.offsetWidth);
      const h = Math.round(el.offsetHeight);
      setGeo((g) => (Math.abs(g.w - w) > 1 || Math.abs(g.h - h) > 1 ? { ...g, w, h } : g));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [geo.mode]);

  const runPrompt = async (text: string): Promise<void> => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true);
    setActivity([]);
    const messages: ChatMessage[] = [{ role: "user", content: q }];
    try {
      await streamAgent({ messages, locale, route }, (evt) => {
        switch (evt.type) {
          case "RUN_STARTED":
            runIdRef.current = evt.runId;
            break;
          case "TOOL_CALL":
            setActivity((a) => [...a, { name: evt.name }]);
            break;
          case "TOOL_RESULT":
            setActivity((a) => {
              const next = a.slice();
              for (let i = next.length - 1; i >= 0; i--) {
                if (next[i]!.name === evt.name && next[i]!.ok === undefined) {
                  next[i] = { name: evt.name, ok: evt.ok, summary: evt.summary };
                  return next;
                }
              }
              return [...next, { name: evt.name, ok: evt.ok, summary: evt.summary }];
            });
            break;
          case "UI_NAVIGATE":
            navigate(evt.path);
            break;
          case "DATA_CHANGED":
            refetch();
            break;
          // TEXT_MESSAGE_CONTENT is intentionally ignored — there is no prose transcript.
        }
      });
    } catch {
      setActivity((a) => [...a, { name: "error", ok: false, summary: t("genui.error") }]);
    } finally {
      setBusy(false);
      runIdRef.current = null;
    }
  };

  // The composer send clears the input; an action button dispatches its own text.
  const send = (): void => {
    const q = prompt.trim();
    if (!q || busy) return;
    setPrompt("");
    void runPrompt(q);
  };

  const handlers: BlockHandlers = {
    onRemove: (id) => void scratchpadOp({ op: "remove", blockId: id }),
    onMove: (id, dir) => {
      const idx = blocks.findIndex((b) => b.id === id);
      if (idx < 0) return;
      void scratchpadOp({ op: "move", blockId: id, toIndex: idx + dir });
    },
    onUpdate: (id, data: BlockData) => void scratchpadOp({ op: "update", blockId: id, block: data }),
    onAction: (text) => void runPrompt(text),
  };

  const addNote = (): void => {
    const text = window.prompt(t("genui.addNote"));
    if (text && text.trim()) void scratchpadOp({ op: "add", block: { kind: "prose", text: text.trim() } });
  };

  // ── Floating drag ────────────────────────────────────────────────────────────
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  const onHeaderDown = (e: React.PointerEvent): void => {
    if (geo.mode !== "floating") return;
    dragRef.current = { dx: e.clientX - geo.x, dy: e.clientY - geo.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onHeaderMove = (e: React.PointerEvent): void => {
    if (!dragRef.current) return;
    const x = Math.max(0, Math.min(window.innerWidth - 120, e.clientX - dragRef.current.dx));
    const y = Math.max(0, Math.min(window.innerHeight - 60, e.clientY - dragRef.current.dy));
    setGeo((g) => ({ ...g, x, y }));
  };
  const onHeaderUp = (e: React.PointerEvent): void => {
    dragRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // ── Dock-width resize ────────────────────────────────────────────────────────
  const resizeRef = useRef(false);
  const onResizeDown = (e: React.PointerEvent): void => {
    resizeRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onResizeMove = (e: React.PointerEvent): void => {
    if (!resizeRef.current) return;
    // Docked on the RIGHT: width grows as the dragged left edge moves leftward.
    setGeo((g) => ({ ...g, dockW: Math.max(280, Math.min(window.innerWidth - 320, window.innerWidth - e.clientX)) }));
  };
  const onResizeUp = (): void => {
    resizeRef.current = false;
  };

  const setMode = (mode: Mode): void => withTransition(() => flushSync(() => setGeo((g) => ({ ...g, mode }))));

  if (geo.mode === "collapsed") {
    return (
      <button className="genui-launcher" style={{ viewTransitionName: "genui-panel" }} onClick={() => setMode("floating")} title={t("genui.open")}>
        ✦ {t("genui.title")}
        {blocks.length > 0 ? <span className="genui-launcher-count">{blocks.length}</span> : null}
      </button>
    );
  }

  const floating = geo.mode === "floating";
  const style: React.CSSProperties = floating
    ? { left: geo.x, top: geo.y, width: geo.w, height: geo.h, viewTransitionName: "genui-panel" }
    : { width: geo.dockW, viewTransitionName: "genui-panel" };

  return (
    <aside className={`genui-panel ${geo.mode}`} style={style} ref={panelRef}>
      <header
        className="genui-head"
        onPointerDown={onHeaderDown}
        onPointerMove={onHeaderMove}
        onPointerUp={onHeaderUp}
        style={{ cursor: floating ? "grab" : "default" }}
      >
        <span className="genui-title">✦ {t("genui.title")}</span>
        <span className="genui-head-spacer" />
        <button className="genui-ctl" title={floating ? t("genui.dock") : t("genui.float")} onClick={() => setMode(floating ? "docked" : "floating")}>
          {floating ? "⇥" : "⇤"}
        </button>
        <button className="genui-ctl" title={t("genui.collapse")} onClick={() => setMode("collapsed")}>
          –
        </button>
      </header>

      <div className="genui-body" ref={bodyRef}>
        {blocks.length === 0 ? (
          <div className="genui-empty">{t("genui.intro")}</div>
        ) : ws ? (
          <div className="genui-blocks">
            {blocks.map((b, i) => (
              <BlockView key={b.id} ws={ws} block={b} index={i} count={blocks.length} handlers={handlers} />
            ))}
          </div>
        ) : null}

        {busy || activity.length > 0 ? (
          <div className="genui-activity">
            {activity.map((a, i) => (
              <div key={i} className={`genui-act ${a.ok === false ? "bad" : a.ok ? "ok" : "run"}`}>
                <span className="genui-act-icon">{a.ok === undefined ? "↻" : a.ok ? "✓" : "✕"}</span>
                <span className="genui-act-name">{a.name}</span>
                {a.summary ? <span className="genui-act-sum">{a.summary}</span> : null}
              </div>
            ))}
            {busy ? <div className="genui-act run"><span className="genui-act-icon">✦</span><span className="genui-act-name">{t("genui.working")}</span></div> : null}
          </div>
        ) : null}
      </div>

      <footer className="genui-foot">
        <button className="genui-ctl note" title={t("genui.addNote")} onClick={addNote}>＋</button>
        <AutoTextarea
          className="chat-input"
          rows={1}
          value={prompt}
          placeholder={t("genui.placeholder")}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {busy ? (
          <button className="btn warn sm" onClick={() => runIdRef.current && void cancelAgent(runIdRef.current)} title={t("console.stop")}>
            {t("console.stop")}
          </button>
        ) : (
          <button className="btn primary sm" onClick={() => void send()}>
            {t("console.send")}
          </button>
        )}
      </footer>

      {!floating ? (
        <div
          className="genui-resize"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          aria-hidden="true"
        />
      ) : null}
    </aside>
  );
}
